import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Toast from "../Toast/Toast";
import "./dashboard.css";
import { apiFetch } from "../config/apiClient";
import { clearAuth, getUser } from "../config/auth";
import { listQuizAttemptsForUser } from "../config/quizAttemptCache";

const titleCase = (value = "") =>
  String(value || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatAttemptTime = (value) => {
  if (!value) return "Recently attempted";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently attempted";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseAttemptRoute = (attempt) => {
  const key = String(attempt?.attemptKey || "");

  if (key.startsWith("topic::")) {
    const [, subject, topic, slug, id] = key.split("::");
    return {
      kind: "Quiz Zone",
      title: titleCase(slug),
      subtitle: `${titleCase(subject)} • ${titleCase(topic)}`,
      path: `/quiz-zone/${encodeURIComponent(subject || "")}/${encodeURIComponent(
        topic || ""
      )}/${encodeURIComponent(slug || "")}/start?id=${encodeURIComponent(id || "")}&lang=hi`,
    };
  }

  if (key.startsWith("ca::")) {
    const slugWithId = key.slice(4);
    const cleanedTitle = slugWithId.replace(/-\d+$/, "");
    return {
      kind: "Current Affairs",
      title: titleCase(cleanedTitle),
      subtitle: "Daily Current Affairs Quiz",
      path: `/daily-current-affairs-quiz/${encodeURIComponent(slugWithId)}/start?lang=hi`,
    };
  }

  return {
    kind: "Quiz",
    title: "Attempted Quiz",
    subtitle: "Saved attempt",
    path: "/quiz-zone",
  };
};

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    const parsed = getUser();
    if (!parsed) {
      clearAuth();
      navigate("/login");
      return;
    }

    const role = String(parsed?.role || "STUDENT").toUpperCase();
    if (role === "ADMIN") {
      navigate("/admin/dashboard");
      return;
    }

    setUser(parsed);
  }, [navigate]);

  const attempts = useMemo(() => {
    if (!user?.id) return [];
    return listQuizAttemptsForUser(user.id).map((attempt) => {
      const route = parseAttemptRoute(attempt);
      const totalMarks = Math.max(0, Number(attempt.totalMarks) || 0);
      const score = Math.max(0, Number(attempt.score) || 0);
      const percent = totalMarks ? Math.round((score / totalMarks) * 100) : 0;
      return {
        ...attempt,
        ...route,
        totalMarks,
        score,
        percent,
      };
    });
  }, [user?.id]);

  const summary = useMemo(() => {
    const totalAttempts = attempts.length;
    const bestScore = attempts.reduce((best, attempt) => Math.max(best, attempt.percent), 0);
    const averageScore = totalAttempts
      ? Math.round(
          attempts.reduce((sum, attempt) => sum + attempt.percent, 0) / totalAttempts
        )
      : 0;
    return { totalAttempts, bestScore, averageScore };
  }, [attempts]);

  const handleLogout = async () => {
    try {
      await apiFetch("/users/logout", {
        method: "POST",
        auth: true,
        skipUnauthorizedHandler: true,
      });
    } catch {
      // Proceed with local logout even if server logout fails.
    } finally {
      clearAuth();
      setToastMsg("Logged out successfully!");

      setTimeout(() => {
        setToastMsg("");
        navigate("/");
      }, 1200);
    }
  };

  return (
    <div className="dashboard-shell">
      <Helmet>
        <title>Student Dashboard | AAMSchool</title>
        <link rel="canonical" href="https://www.aamschool.in/dashboard" />
        <meta
          name="description"
          content="Access your AAMSchool student dashboard to review attempted quizzes and track preparation progress."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="dashboard-kicker">Student Space</span>
          <h1>Dashboard</h1>
          <p>
            Track your progress, jump back into attempted quizzes, and keep your preparation moving.
          </p>
        </div>
        <div className="dashboard-hero-actions">
          <button onClick={() => navigate("/quiz-zone")} className="btn-primary">
            Open Quiz Zone
          </button>
          <button
            onClick={() => navigate("/daily-current-affairs-quiz")}
            className="btn-ghost"
          >
            Current Affairs
          </button>
        </div>
      </section>

      <section className="dashboard-grid">
        <div className="dashboard-panel dashboard-profile-panel">
          <div className="dashboard-panel-head">
            <h2>Your Profile</h2>
            <span className="dashboard-role-pill">{user?.role || "STUDENT"}</span>
          </div>
          <p className="dashboard-greeting">
            Hello, <strong>{user?.username || "Student"}</strong>
          </p>
          <div className="dashboard-profile-list">
            <div className="dashboard-profile-row">
              <span>Email</span>
              <strong>{user?.email || "NA"}</strong>
            </div>
            <div className="dashboard-profile-row">
              <span>User ID</span>
              <strong>{user?.id ?? "NA"}</strong>
            </div>
            <div className="dashboard-profile-row">
              <span>Plan</span>
              <strong>{user?.subscriptionPlan || "FREE"}</strong>
            </div>
            <div className="dashboard-profile-row">
              <span>Status</span>
              <strong>{user?.blocked ? "Blocked" : "Active"}</strong>
            </div>
          </div>

          <div className="dashboard-actions">
            <button onClick={() => navigate("/")} className="btn-ghost">
              Back to Home
            </button>
            <button onClick={handleLogout} className="btn-secondary">
              Logout
            </button>
          </div>
        </div>

        <div className="dashboard-panel dashboard-stats-panel">
          <div className="dashboard-panel-head">
            <h2>Progress Snapshot</h2>
          </div>
          <div className="dashboard-stat-grid">
            <div className="dashboard-stat-card">
              <span>Total Attempts</span>
              <strong>{summary.totalAttempts}</strong>
            </div>
            <div className="dashboard-stat-card">
              <span>Best Score</span>
              <strong>{summary.bestScore}%</strong>
            </div>
            <div className="dashboard-stat-card">
              <span>Average Score</span>
              <strong>{summary.averageScore}%</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-panel dashboard-attempts-panel">
        <div className="dashboard-panel-head">
          <h2>Attempted Quizzes</h2>
          <span className="dashboard-list-count">{attempts.length}</span>
        </div>

        {attempts.length === 0 ? (
          <div className="dashboard-empty-state">
            <p>No attempted quizzes yet. Start one from Quiz Zone or Current Affairs.</p>
            <button onClick={() => navigate("/quiz-zone")} className="btn-primary">
              Start Practicing
            </button>
          </div>
        ) : (
          <div className="dashboard-attempt-list">
            {attempts.map((attempt) => (
              <article
                key={attempt.attemptKey}
                className={`dashboard-attempt-card ${
                  attempt.percent >= 60 ? "is-success" : "is-warning"
                }`}
                style={{ "--attempt-fill": `${attempt.percent}%` }}
              >
                <div className="dashboard-attempt-main">
                  <div className="dashboard-attempt-copy">
                    <span className="dashboard-attempt-kind">{attempt.kind}</span>
                    <h3>{attempt.title}</h3>
                    <p>{attempt.subtitle}</p>
                  </div>
                  <div className="dashboard-attempt-score">
                    <strong>{attempt.percent}%</strong>
                    <span>
                      {attempt.score} / {attempt.totalMarks || 0}
                    </span>
                  </div>
                </div>

                <div className="dashboard-attempt-meta">
                  <span>
                    {Number(attempt.correctAnswers || 0)} correct • {formatDuration(attempt.timeTakenSeconds)}
                  </span>
                  <span>{formatAttemptTime(attempt.submittedAt || attempt.attemptedAt)}</span>
                </div>

                <div className="dashboard-attempt-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => navigate(attempt.path)}
                  >
                    Reattempt Quiz
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Toast message={toastMsg} onClose={() => setToastMsg("")} />
    </div>
  );
}

export default Dashboard;
